import type { Button } from '@ohif/core/types';
import { ICONS } from '../Icon/getIcon';
import { ViewportGridService } from '@ohif/core';
import type { RunCommand } from '@ohif/core/types';
import { EVENTS } from '@cornerstonejs/core';
import { parseSearchParams } from '@ohif/app/src/utils/parseSearchParams';

const role =
  typeof window === 'undefined'
    ? ''
    : parseSearchParams(window.location.search, true).get('role') ?? '';
const normalizedRole = role.toLowerCase().replace(/[^a-z0-9]/g, '');
const canViewDicomTagBrowser = normalizedRole === 'admin' || normalizedRole === 'superadmin';

export const setToolActiveToolbar = {
  commandName: 'setViewportLayout',
  commandOptions: {
    toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'],
  },
};

const ReferenceLinesListeners: RunCommand = [
  {
    commandName: 'setSourceViewportForReferenceLinesTool',
    context: 'CORNERSTONE',
  },
];

export const toolbarButtons: Button[] = [
  {
    title: 'General Tools',
    icons: [
      {
        id: 'resetAll',
        label: 'Reset All',
        description: "This will reset all the viewports to their default state",
        icon: ICONS['tool-reset-all'],
        uiType: 'ohif.radioGroup',
        commands: 'resetAll',
        evaluate: 'evaluate.action',
      },
      {
        id: 'Reset',
        label: 'Reset Viewport',
        description: "This will reset the current viewport to its default state",
        icon: ICONS['tool-reset'],
        tooltip: 'Reset View',
        commands: 'resetViewport',
        evaluate: 'evaluate.action123',
      },
      {
        id: 'OriginalView',
        label: 'Original View',
        description: "This will return to the original image view",
        icon: ICONS['layout-common-1x1'],
        uiType: 'ohif.radioGroup',
        commands: 'setHangingProtocol',
        evaluate: 'evaluate.action',
        props: {
          protocolId: 'default',
          commandName: 'setHangingProtocol',
          commandOptions: { toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] },
          refreshProps: {
            viewportId: 'default',
          },
        },
      },
      {
        id: 'rotate-right',
        label: 'Rotate Right',
        description: "This will rotate the current viewport clockwise",
        icon: ICONS['tool-rotate-right'],
        uiType: 'ohif.radioGroup',
        commands: 'rotateViewportCW',
        evaluate: 'evaluate.action',
      },
      {
        id: 'flipHorizontal',
        label: 'Flip Horizontal',
        description: "This will flip the current viewport horizontally",
        icon: ICONS['tool-flip-horizontal'],
        uiType: 'ohif.radioGroup',
        commands: 'flipViewportHorizontal',
        evaluate: 'evaluate.viewportProperties.toggle',
      },
      {
        id: 'ToggleOverlay',
        label: 'DICOM Overlay',
        description: "This will toggle the DICOM overlay on the current viewport",
        icon: ICONS['toggle-dicom-overlay'],
        uiType: 'ohif.radioGroup',
        commands: 'toggleOverlay',
        evaluate: 'evaluate.action',
      },
      {
        id: 'StackScroll',
        label: 'Stack Scroll',
        description: "This will enable stack scrolling on the current viewport",
        icon: ICONS['tool-stack-scroll'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'invert',
        label: 'Invert Colors',
        description: "This will invert the colors of the current viewport",
        icon: ICONS['tool-invert'],
        uiType: 'ohif.radioGroup',
        commands: 'invertViewport',
        evaluate: 'evaluate.viewportProperties.toggle',
      },
      {
        id: 'Cine',
        label: 'Cine',
        description: "This will toggle the cine mode on the current viewport",
        icon: ICONS['tool-cine'],
        uiType: 'ohif.radioGroup',
        commands: 'toggleCine',
        evaluate: ['evaluate.cine', 'evaluate.not3D'],
      },
      {
        id: 'Zoom',
        label: 'Zoom',
        description: "This will enable zooming on the current viewport",
        icon: ICONS['tool-zoom'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'CalibrationLine',
        label: 'Calibration',
        description: "This will enable the calibration tool on the current viewport",
        icon: ICONS['tool-calibration'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      ...(canViewDicomTagBrowser
        ? [
            {
              id: 'TagBrowser',
              label: 'DICOM Tag Browser',
              description: 'This will open the DICOM Tag Browser',
              icon: ICONS['dicom-tag-browser'],
              uiType: 'ohif.radioGroup',
              commands: 'openDICOMTagViewer',
              evaluate: 'evaluate.cornerstoneTool',
            },
          ]
        : []),
      {
        id: 'Magnify',
        label: 'Magnifier',
        description: "This will enable the magnify tool on the current viewport",
        icon: ICONS['tool-magnify'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'AdvancedMagnify',
        label: 'Magnify Probe',
        description: "This will enable the magnify probe tool on the current viewport",
        icon: ICONS['icon-tool-loupe'],
        uiType: 'ohif.radioGroup',
        commands: 'toggleActiveDisabledToolbar',
        evaluate: 'evaluate.cornerstoneTool.toggle.ifStrictlyDisabled',
        props: {
          commands: 'toggleActiveDisabledToolbar',
          itemId: 'AdvancedMagnify',
          refreshProps: { viewportId: 'default' },
        },
      },
      {
        id: 'UltrasoundDirectionalTool',
        label: 'Ultrasound Bidirectional',
        description: "This will enable the ultrasound bidirectional tool on the current viewport",
        icon: ICONS['icon-tool-ultrasound-bidirectional'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'WindowLevelRegion',
        label: 'Window Region',
        description: "This will enable the window region tool on the current viewport",
        icon: ICONS['icon-tool-window-region'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'TMTVViewer',
        label: 'TMTV mode',
        description: "This will enable the TMTV viewer layout",
        icon: ICONS['tool-tmtv-viewer'],
        uiType: 'ohif.radioGroup',
        commands: 'openTMTVLayout',
        evaluate: 'evaluate.action',
      },
    ],
  },
  {
    title: 'Measurement & Annotation',
    icons: [
      {
        id: 'Length',
        label: 'Length',
        description: "This will enable the length measurement tool on the current viewport",
        icon: ICONS['tool-length'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'Bidirectional',
        label: 'Bidirectional',
        description: "This will enable the bidirectional measurement tool on the current viewport",
        icon: ICONS['tool-bidirectional'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'TriPlanarVolume',
        label: '3-Plane Volume',
        description: "This will enable the 3-plane / multiplanar volume measurement tool on the current viewport",
        icon: ICONS['tool-ellipse'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      // {
      //   id: 'SpineLabeling',
      //   label: 'Spine Labeling',
      //   description: "This will enable the spine labeling tool on the current viewport",
      //   icon: ICONS['spine-label'],
      //   uiType: 'ohif.radioGroup',
      //   commands: 'openSpineLabeling',
      //   evaluate: 'evaluate.action',
      // },
      {
        id: 'ArrowAnnotate',
        label: 'Annotation',
        description: "This will enable the arrow annotation tool on the current viewport",
        icon: ICONS['tool-annotate'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'SplineROI',
        label: 'Spline ROI',
        description: "This will enable the spline ROI tool on the current viewport",
        icon: ICONS['icon-tool-spline-roi'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'CurvedDistance',
        label: 'Curved Distance',
        description: "Calculate curved length/distance along an open spline curve",
        icon: ICONS['icon-tool-curved-distance'] || ICONS['icon-tool-spline-roi'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'EllipticalROI',
        label: 'Ellipse',
        description: "This will enable the ellipse ROI tool on the current viewport",
        icon: ICONS['tool-ellipse'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'RectangleROI',
        label: 'Rectangle',
        description: "This will enable the rectangle ROI tool on the current viewport",
        icon: ICONS['tool-rectangle'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'CircleROI',
        label: 'Circle',
        description: "This will enable the circle ROI tool on the current viewport",
        icon: ICONS['tool-circle'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'PlanarFreehandROI',
        label: 'Freehand ROI',
        description: "This will enable the freehand ROI tool on the current viewport",
        icon: ICONS['icon-tool-freehand-roi'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'LivewireContour',
        label: 'Livewire',
        description: "This will enable the livewire contour tool on the current viewport",
        icon: ICONS['icon-tool-livewire'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'Angle',
        label: 'Angle',
        description: "This will enable the angle measurement tool on the current viewport",
        icon: ICONS['tool-angle'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'CobbAngle',
        label: 'Cobb Angle',
        description: "This will enable the Cobb angle measurement tool on the current viewport",
        icon: ICONS['icon-tool-cobb-angle'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'Probe',
        label: 'Probe',
        description: "This will enable the probe tool on the current viewport",
        icon: ICONS['tool-probe'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'KeyImage',
        label: 'Key Image',
        description: "This will enable the key image tool on the current viewport",
        icon: ICONS['key-image'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
    ],
  },
  {
    title: 'Other',
    icons: [
      {
        id: 'previousImage',
        label: 'Previous Image',
        description: "This will go to the previous image in the stack",
        icon: ICONS['chevron-left'],
        uiType: 'ohif.radioGroup',
        commands: 'previousImage',
        evaluate: 'evaluate.action',
      },
      {
        id: 'nextImage',
        label: 'Next Image',
        description: "This will go to the next image in the stack",
        icon: ICONS['chevron-right'],
        uiType: 'ohif.radioGroup',
        commands: 'nextImage',
        evaluate: 'evaluate.action',
      },
      {
        id: 'History',
        label: 'History',
        description: "This will open the patient history",
        icon: ICONS['tool-history'],
        uiType: 'ohif.radioGroup',
        commands: 'openPatientHistory',
        evaluate: 'evaluate.action',
      },
      // {
      //   id: 'DoctorsList',
      //   label: 'Doctors List',
      //   description: "This will open the doctors list",
      //   icon: ICONS['tool-reassign'],
      //   uiType: 'ohif.radioGroup',
      //   commands: 'openDoctorList',
      //   evaluate: 'evaluate.action',
      // },
      {
        id: 'Report',
        label: 'Report',
        description: "This will open the report for the current study",
        icon: ICONS['tool-report'],
        uiType: 'ohif.radioGroup',
        commands: 'openReport',
        evaluate: 'evaluate.action',
      },
      {
        id: 'CopyImage',
        label: 'Copy',
        description: "This will copy the current image to the clipboard",
        icon: ICONS['tool-copy'],
        uiType: 'ohif.radioGroup',
        commands: 'copyImgToClipboard',
        evaluate: 'evaluate.action',
      },
      {
        id: 'Capture',
        label: 'Capture',
        description: "This will capture the current viewport as an image",
        icon: ICONS['tool-capture'],
        uiType: 'ohif.radioGroup',
        commands: 'showDownloadViewportModal',
        evaluate: 'evaluate.action',
      },
      {
        id: 'Pan',
        label: 'Pan',
        description: "This will enable the pan tool on the current viewport",
        icon: ICONS['tool-move'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        //not working
        id: 'ReferenceCursors',
        label: 'Triangulation',
        description: "This will enable the reference cursors tool on the current viewport",
        icon: ICONS['tool-pointer'],
        uiType: 'ohif.radioGroup',
        commands: 'toggleActiveDisabledToolbar',
        evaluate: 'evaluate.cornerstoneTool.toggle.ifStrictlyDisabled',
        props: {
          refreshProps: { viewportId: 'default' },
          itemId: 'ReferenceCursors',
          commands: 'toggleActiveDisabledToolbar',
        },
      },
      {
        id: 'WindowLevel',
        label: 'Window Level',
        description: "This will enable the window level tool on the current viewport",
        icon: ICONS['tool-window-level'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
    ],
  },
  {
    title: 'Invariable Options',
    icons: [
      {
        id: 'Volume3D',
        label: 'MPR',
        description: "This will enable the MPR tool on the current viewport",
        icon: ICONS['tool-mpr'],
        uiType: 'ohif.radioGroup',
        commands: 'setHangingProtocol',
        evaluate: 'evaluate.action',
        props: {
          protocolId: 'mpr',
          commandName: 'setHangingProtocol',
          commandOptions: { toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] },
          refreshProps: { viewportId: 'default' },
        },
      },
      {
        id: '3DViewer',
        label: '3D Only',
        description: "This will enable the 3D viewer tool on the current viewport",
        icon: ICONS['tool-3d-only'],
        uiType: 'ohif.radioGroup',
        commands: 'setHangingProtocol',
        evaluate: 'evaluate.action',
        props: {
          protocolId: 'only3D',
          commandName: 'setHangingProtocol',
          commandOptions: {
            toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'],
          },
          refreshProps: {
            viewportId: 'default',
          },
        },
      },
      {
        id: 'OriginalView',
        label: 'Original View',
        description: "This will return to the original image view",
        icon: ICONS['icon-toolbar-back'],
        uiType: 'ohif.radioGroup',
        commands: 'setHangingProtocol',
        evaluate: 'evaluate.action',
        props: {
          protocolId: 'default',
          commandName: 'setHangingProtocol',
          commandOptions: { toolGroupIds: ['default', 'mpr', 'SRToolGroup', 'volume3d'] },
          refreshProps: {
            viewportId: 'default',
          },
        },
      },
      {
        id: 'ReferenceLines',
        label: 'Reference Lines',
        description: "This will enable the reference lines tool on the current viewport",
        icon: ICONS['tool-referenceLines'],
        uiType: 'ohif.radioGroup',
        commands: 'toggleEnabledDisabledToolbar',
        evaluate: 'evaluate.cornerstoneTool.toggle',
        props: {
          commands: 'toggleEnabledDisabledToolbar',
          itemId: 'ReferenceLines',
          refreshProps: { viewportId: 'default' },
          listeners: {
            [ViewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED]: ReferenceLinesListeners,
            [ViewportGridService.EVENTS.VIEWPORTS_READY]: ReferenceLinesListeners,
          },
        },
      },
      {
        id: 'ImageSliceSync',
        label: 'Image Slice Sync',
        description: "This will enable the image slice synchronization tool on the current viewport",
        icon: ICONS['link'],
        uiType: 'ohif.radioGroup',
        commands: 'toggleSynchronizer',
        evaluate: 'evaluate.cornerstone.synchronizer',
        props: {
          commands: { commandName: 'toggleSynchronizer', commandOptions: { type: 'imageSlice' } },
          itemId: 'ImageSliceSync',
          refreshProps: { viewportId: 'default' },
          type: 'imageSlice',
          listeners: {
            [EVENTS.STACK_VIEWPORT_NEW_STACK]: {
              commandName: 'toggleImageSliceSync',
              commandOptions: { toggledState: true },
            },
          },
        },
      },
      {
        id: 'Crosshairs',
        label: 'Crosshairs',
        description: "This will enable the crosshairs tool on the MPR viewport",
        icon: ICONS['tool-crosshair'],
        uiType: 'ohif.radioGroup',
        commands: 'setToolActiveToolbar',
        evaluate: 'evaluate.cornerstoneTool',
        props: {
          toolGroupIds: ['mpr'],
          refreshProps: { viewportId: 'mpr-axial' },
          itemId: 'Crosshairs',
          type: 'tool',
          commands: {
            commandName: 'setToolActiveToolbar',
            commandOptions: {
              toolGroupIds: ['mpr'],
            },
          },
          evaluate: {
            name: 'evaluate.cornerstoneTool',
            disabledText: 'Select an MPR viewport to enable this tool',
          },
        },
      },
      {
        id: 'TrackballRotate',
        label: '3D Rotate',
        description: "This will enable the 3D rotate tool on the current viewport",
        icon: ICONS['tool-3d-rotate'],
        uiType: 'ohif.radioGroup',
        commands: 'setViewportLayout',
        props: {
          type: 'tool',
          commands: setToolActiveToolbar,
          evaluate: {
            name: 'evaluate.cornerstoneTool',
            disabledText: 'Select a 3D viewport to enable this tool',
          },
        },
      },
      // {
      //   no requirement
      //   id: 'ImageOverlayViewer',
      //   label: 'Image Overlay',
      //   description: "This will enable the image overlay viewer tool on the current viewport",
      //   icon: ICONS['toggle-dicom-overlay'],
      //   uiType: 'ohif.radioGroup',
      //   commands: 'toggleEnabledDisabledToolbar',
      //   evaluate: 'evaluate.cornerstoneTool.toggle',
      // },
      // {
      //   no requirement
      //   id: 'setVoirange',
      //   label: 'Set Voi Range',
      //   description: "This will enable the VOI range tool on the current viewport",
      //   icon: ICONS['tool-3d-rotate'],
      //   uiType: 'ohif.radioGroup',
      //   commands: 'setVoirange',
      //   evaluate: 'evaluate.action',
      // },
    ],
  },
  {
    title: 'Favorite Tools',
    icons: [],
  },
  {
    title: 'Mobile View',
    icons: [
      {
        id: 'resetAll',
        label: 'Reset All',
        description: "This will reset all the viewports to their default state",
        icon: ICONS['tool-reset-all'],
        uiType: 'ohif.radioGroup',
        commands: 'resetAll',
        evaluate: 'evaluate.action',
      },
      {
        id: 'Reset',
        label: 'Reset Viewport',
        description: "This will reset the current viewport to its default state",
        icon: ICONS['tool-reset'],
        tooltip: 'Reset View',
        commands: 'resetViewport',
        evaluate: 'evaluate.action123',
      },
      {
        id: 'rotate-right',
        label: 'Rotate Right',
        description: "This will rotate the current viewport clockwise",
        icon: ICONS['tool-rotate-right'],
        uiType: 'ohif.radioGroup',
        commands: 'rotateViewportCW',
        evaluate: 'evaluate.action',
      },
      {
        id: 'flipHorizontal',
        label: 'Flip Horizontal',
        description: "This will flip the current viewport horizontally",
        icon: ICONS['tool-flip-horizontal'],
        uiType: 'ohif.radioGroup',
        commands: 'flipViewportHorizontal',
        evaluate: 'evaluate.viewportProperties.toggle',
      },
      {
        id: 'Length',
        label: 'Length',
        description: "This will enable the length measurement tool on the current viewport",
        icon: ICONS['tool-length'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'Bidirectional',
        label: 'Bidirectional',
        description: "This will enable the bidirectional measurement tool on the current viewport",
        icon: ICONS['tool-bidirectional'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'ArrowAnnotate',
        label: 'Annotation',
        description: "This will enable the arrow annotation tool on the current viewport",
        icon: ICONS['tool-annotate'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'Angle',
        label: 'Angle',
        description: "This will enable the angle measurement tool on the current viewport",
        icon: ICONS['tool-angle'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'SplineROI',
        label: 'Spline ROI',
        description: "This will enable the spline ROI tool on the current viewport",
        icon: ICONS['icon-tool-spline-roi'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'CurvedDistance',
        label: 'Curved Distance',
        description: "Calculate curved length/distance along an open spline curve",
        icon: ICONS['icon-tool-curved-distance'] || ICONS['icon-tool-spline-roi'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'PlanarFreehandROI',
        label: 'Freehand ROI',
        description: "This will enable the freehand ROI tool on the current viewport",
        icon: ICONS['icon-tool-freehand-roi'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'RectangleROI',
        label: 'Rectangle',
        description: "This will enable the rectangle ROI tool on the current viewport",
        icon: ICONS['tool-rectangle'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'CobbAngle',
        label: 'Cobb Angle',
        description: "This will enable the Cobb angle measurement tool on the current viewport",
        icon: ICONS['icon-tool-cobb-angle'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'rotate-right',
        label: 'Rotate Right',
        description: "This will rotate the current viewport clockwise",
        icon: ICONS['tool-rotate-right'],
        uiType: 'ohif.radioGroup',
        commands: 'rotateViewportCW',
        evaluate: 'evaluate.action',
      },
      {
        id: 'flipHorizontal',
        label: 'Flip Horizontal',
        description: "This will flip the current viewport horizontally",
        icon: ICONS['tool-flip-horizontal'],
        uiType: 'ohif.radioGroup',
        commands: 'flipViewportHorizontal',
        evaluate: 'evaluate.viewportProperties.toggle',
      },
      {
        id: 'StackScroll',
        label: 'Stack Scroll',
        description: "This will enable stack scrolling on the current viewport",
        icon: ICONS['tool-stack-scroll'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'History',
        label: 'History',
        description: "This will open the patient history",
        icon: ICONS['tool-history'],
        uiType: 'ohif.radioGroup',
        commands: 'openPatientHistory',
        evaluate: 'evaluate.action',
      },
      {
        id: 'previousImage',
        label: 'Previous Image',
        description: "This will go to the previous image in the stack",
        icon: ICONS['chevron-left'],
        uiType: 'ohif.radioGroup',
        commands: 'previousImage',
        evaluate: 'evaluate.action',
      },
      {
        id: 'nextImage',
        label: 'Next Image',
        description: "This will go to the next image in the stack",
        icon: ICONS['chevron-right'],
        uiType: 'ohif.radioGroup',
        commands: 'nextImage',
        evaluate: 'evaluate.action',
      },
      {
        id: 'WindowLevel',
        label: 'Window Level',
        description: "This will enable the window level tool on the current viewport",
        icon: ICONS['tool-window-level'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'Zoom',
        label: 'Zoom',
        description: "This will enable zooming on the current viewport",
        icon: ICONS['tool-zoom'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'Pan',
        label: 'Pan',
        description: "This will enable the pan tool on the current viewport",
        icon: ICONS['tool-move'],
        uiType: 'ohif.radioGroup',
        commands: 'DEAFULT__TOOL',
        evaluate: 'evaluate.cornerstoneTool',
      },
      {
        id: 'Crosshairs',
        label: 'Crosshairs',
        description: "This will enable the crosshairs tool on the MPR viewport",
        icon: ICONS['tool-crosshair'],
        uiType: 'ohif.radioGroup',
        commands: 'setToolActiveToolbar',
        evaluate: 'evaluate.cornerstoneTool',
        props: {
          toolGroupIds: ['mpr'],
          refreshProps: { viewportId: 'mpr-axial' },
          itemId: 'Crosshairs',
          type: 'tool',
          commands: {
            commandName: 'setToolActiveToolbar',
            commandOptions: {
              toolGroupIds: ['mpr'],
            },
          },
          evaluate: {
            name: 'evaluate.cornerstoneTool',
            disabledText: 'Select an MPR viewport to enable this tool',
          },
        },
      },
      {
        id: 'ReferenceLines',
        label: 'Reference Lines',
        description: "This will enable the reference lines tool on the current viewport",
        icon: ICONS['tool-referenceLines'],
        uiType: 'ohif.radioGroup',
        commands: 'toggleEnabledDisabledToolbar',
        evaluate: 'evaluate.cornerstoneTool.toggle',
        props: {
          commands: 'toggleEnabledDisabledToolbar',
          itemId: 'ReferenceLines',
          refreshProps: { viewportId: 'default' },
          listeners: {
            [ViewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED]: ReferenceLinesListeners,
            [ViewportGridService.EVENTS.VIEWPORTS_READY]: ReferenceLinesListeners,
          },
        },
      },
    ]
  },
];
